import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TablePagination,
  Chip,
  Tooltip,
  Grid,
  InputAdornment,
  FormHelperText,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ManualIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import { UserContext } from "../../../contexts/UserContext";
import { useSnackbar } from "notistack";
import { validateRut } from "../../../utils/validations";
import HistoryIcon from "@mui/icons-material/History";
import SignerHistoryModal from "./SignerHistoryModal";

function FirmantesPage() {
  const { token } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [firmantes, setFirmantes] = useState([]);
  const [filteredFirmantes, setFilteredFirmantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // "all", "manual", "system"
  const [filterActive, setFilterActive] = useState("all"); // "all", "active", "inactive"

  // Estado para editar firmante
  const [editingFirmante, setEditingFirmante] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);

  // Estados para el modal de firmante manual
  const [openManualModal, setOpenManualModal] = useState(false);
  const [manualFirmante, setManualFirmante] = useState({
    first_name: "",
    last_name: "",
    rut: "",
    email: "",
    signing_order: 1,
    is_active: 1,
  });
  const [manualErrors, setManualErrors] = useState({});

  // Estados para el modal de firmante basado en usuario
  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [signingOrder, setSigningOrder] = useState(1);
  const [userErrors, setUserErrors] = useState({});

  // Estado para el mensaje de confirmación
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedFirmanteForHistory, setSelectedFirmanteForHistory] =
    useState(null);

  // Obtener datos iniciales
  useEffect(() => {
    fetchData();
  }, [token]);

  // Efecto para filtrar firmantes cuando cambian los filtros
  useEffect(() => {
    applyFilters();
  }, [firmantes, searchTerm, filterType, filterActive]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Obtener firmantes
      const firmantesResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/signers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!firmantesResponse.ok) {
        throw new Error("Error al cargar firmantes");
      }

      const firmantesData = await firmantesResponse.json();
      setFirmantes(firmantesData.data || []);
      setFilteredFirmantes(firmantesData.data || []);

      // Obtener usuarios para seleccionar
      const usuariosResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!usuariosResponse.ok) {
        throw new Error("Error al cargar usuarios");
      }

      const usuariosData = await usuariosResponse.json();
      setUsuarios(usuariosData.users || []);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Error al cargar datos: " + error.message, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtrado de firmantes
  const applyFilters = () => {
    let result = [...firmantes];

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (firmante) =>
          firmante.first_name?.toLowerCase().includes(search) ||
          firmante.last_name?.toLowerCase().includes(search) ||
          firmante.rut?.toLowerCase().includes(search) ||
          firmante.email?.toLowerCase().includes(search)
      );
    }

    // Aplicar filtro por tipo
    if (filterType !== "all") {
      const isManual = filterType === "manual";
      result = result.filter(
        (firmante) => (firmante.is_manual === 1) === isManual
      );
    }

    // Aplicar filtro por estado
    if (filterActive !== "all") {
      const isActive = filterActive === "active";
      result = result.filter(
        (firmante) => Boolean(firmante.is_active) === isActive
      );
    }

    setFilteredFirmantes(result);
    setPage(0); // Reiniciar a la primera página cuando cambian los filtros
  };

  // Resetear todos los filtros
  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterActive("all");
  };

  // Validación de RUT chileno
  const validateChileanRut = (rut) => {
    if (!rut) return { isValid: false, message: "El RUT es requerido" };

    // Eliminar puntos y guión
    const rutClean = rut.replace(/[.-]/g, "");

    // Obtener dígito verificador
    const dv = rutClean.slice(-1);
    const rutNumber = parseInt(rutClean.slice(0, -1), 10);

    if (isNaN(rutNumber)) {
      return { isValid: false, message: "El RUT debe contener solo números" };
    }

    // Algoritmo para calcular dígito verificador
    let suma = 0;
    let multiplo = 2;

    // Para cada dígito, multiplicar por el multiplo correspondiente y sumar
    for (let i = String(rutNumber).length - 1; i >= 0; i--) {
      suma += String(rutNumber).charAt(i) * multiplo;
      multiplo = multiplo === 7 ? 2 : multiplo + 1;
    }

    // Calcular dígito verificador
    const dvEsperado = 11 - (suma % 11);
    let dvCalculado;

    if (dvEsperado === 11) dvCalculado = "0";
    else if (dvEsperado === 10) dvCalculado = "K";
    else dvCalculado = String(dvEsperado);

    // Verificar si el dígito verificador coincide
    if (dv.toUpperCase() !== dvCalculado) {
      return { isValid: false, message: "El RUT no es válido" };
    }

    return { isValid: true, message: "RUT válido" };
  };

  // Funciones para firmantes manuales
  const handleManualInputChange = (e) => {
    const { name, value } = e.target;
    setManualFirmante((prev) => ({ ...prev, [name]: value }));

    // Validaciones específicas para cada campo
    if (name === "rut") {
      const validation = validateChileanRut(value);
      if (!validation.isValid) {
        setManualErrors((prev) => ({ ...prev, rut: validation.message }));
      } else {
        setManualErrors((prev) => ({ ...prev, rut: null }));
      }
    } else if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setManualErrors((prev) => ({ ...prev, email: "Email no válido" }));
      } else {
        setManualErrors((prev) => ({ ...prev, email: null }));
      }
    } else if (name === "first_name" || name === "last_name") {
      if (!value.trim()) {
        setManualErrors((prev) => ({
          ...prev,
          [name]: "Este campo es requerido",
        }));
      } else {
        setManualErrors((prev) => ({ ...prev, [name]: null }));
      }
    }
  };

  const validateManualForm = () => {
    const errors = {};
    if (!manualFirmante.first_name.trim())
      errors.first_name = "El nombre es requerido";
    if (!manualFirmante.last_name.trim())
      errors.last_name = "El apellido es requerido";

    const rutValidation = validateChileanRut(manualFirmante.rut);
    if (!rutValidation.isValid) errors.rut = rutValidation.message;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(manualFirmante.email))
      errors.email = "Email no válido";

    // Verificar duplicados
    const isDuplicateRut = firmantes.some(
      (f) =>
        f.rut === manualFirmante.rut &&
        (!editingFirmante || f.id !== editingFirmante.id)
    );

    if (isDuplicateRut)
      errors.rut = "Este RUT ya está registrado como firmante";

    const isDuplicateEmail = firmantes.some(
      (f) =>
        f.email === manualFirmante.email &&
        (!editingFirmante || f.id !== editingFirmante.id)
    );

    if (isDuplicateEmail)
      errors.email = "Este email ya está registrado como firmante";

    setManualErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatRut = (rut) => {
    if (!rut) return "";

    // Eliminar puntos y guión
    let valor = rut.replace(/\./g, "").replace(/-/g, "");

    // Obtener dígito verificador
    const dv = valor.slice(-1);
    let rutNumero = valor.slice(0, -1);

    // Formatear con puntos
    rutNumero = rutNumero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return `${rutNumero}-${dv}`;
  };

  const handleAddManualFirmante = async () => {
    if (!validateManualForm()) return;

    try {
      const formattedManualFirmante = {
        ...manualFirmante,
        rut: manualFirmante.rut.replace(/\./g, ""), // Eliminar puntos para almacenar
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signers/manual`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formattedManualFirmante),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Error al agregar firmante manual"
        );
      }

      await response.json();
      enqueueSnackbar("Firmante manual agregado correctamente", {
        variant: "success",
      });

      // Actualizar lista de firmantes
      await fetchData();

      // Cerrar modal y resetear formulario
      setOpenManualModal(false);
      setManualFirmante({
        first_name: "",
        last_name: "",
        rut: "",
        email: "",
        signing_order: 1,
        is_active: 1,
      });
      setManualErrors({});
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message || "Error al agregar firmante manual", {
        variant: "error",
      });
    }
  };

  // Funciones para firmantes basados en usuarios
  const validateUserForm = () => {
    const errors = {};
    if (!selectedUser) errors.user = "Debe seleccionar un usuario";

    // Verificar si el usuario ya es firmante
    const isDuplicateUser = firmantes.some(
      (f) => f.user_id === selectedUser && !f.is_manual
    );

    if (isDuplicateUser)
      errors.user = "Este usuario ya está registrado como firmante";

    setUserErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddUserFirmante = async () => {
    if (!validateUserForm()) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/signers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedUser,
          signing_order: signingOrder,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error al agregar firmante");
      }

      await response.json();
      enqueueSnackbar("Firmante agregado correctamente", {
        variant: "success",
      });

      // Actualizar lista de firmantes
      await fetchData();

      // Cerrar modal y resetear formulario
      setOpenUserModal(false);
      setSelectedUser("");
      setSigningOrder(1);
      setUserErrors({});
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message || "Error al agregar firmante", {
        variant: "error",
      });
    }
  };

  // Editar firmante
  const handleEditFirmante = (firmante) => {
    if (firmante.is_manual) {
      setManualFirmante({
        first_name: firmante.first_name || "",
        last_name: firmante.last_name || "",
        rut: firmante.rut || "",
        email: firmante.email || "",
        signing_order: firmante.signing_order || 1,
        is_active: firmante.is_active || 0,
      });
      setEditingFirmante(firmante);
      setOpenManualModal(true);
    } else {
      // Para firmantes de usuario solo podemos editar el orden y estado
      setEditingFirmante(firmante);
      setSigningOrder(firmante.signing_order || 1);
      setOpenEditModal(true);
    }
  };

  const handleUpdateFirmante = async () => {
    try {
      // Para firmante manual
      if (editingFirmante.is_manual) {
        if (!validateManualForm()) return;

        const formattedManualFirmante = {
          ...manualFirmante,
          rut: manualFirmante.rut.replace(/\./g, ""), // Eliminar puntos para almacenar
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signers/${editingFirmante.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formattedManualFirmante),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Error al actualizar firmante manual"
          );
        }
      } else {
        // Para firmante basado en usuario
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signers/${editingFirmante.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              signing_order: signingOrder,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error al actualizar firmante");
        }
      }

      enqueueSnackbar("Firmante actualizado correctamente", {
        variant: "success",
      });

      // Actualizar lista de firmantes
      await fetchData();

      // Cerrar modales y resetear estados
      setOpenManualModal(false);
      setOpenEditModal(false);
      setEditingFirmante(null);
      setManualFirmante({
        first_name: "",
        last_name: "",
        rut: "",
        email: "",
        signing_order: 1,
        is_active: 1,
      });
      setManualErrors({});
      setSigningOrder(1);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message || "Error al actualizar firmante", {
        variant: "error",
      });
    }
  };

  // Actualizar estado de firmante (activo/inactivo)
  const toggleActiveFirmante = async (id, currentActive) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signers/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_active: currentActive ? 0 : 1,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Error al actualizar estado del firmante");
      }

      // Actualizar lista de firmantes
      const updatedFirmantes = firmantes.map((firmante) =>
        firmante.id === id
          ? { ...firmante, is_active: currentActive ? 0 : 1 }
          : firmante
      );

      setFirmantes(updatedFirmantes);
      enqueueSnackbar("Estado del firmante actualizado", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Error al actualizar estado del firmante", {
        variant: "error",
      });
    }
  };

  // Eliminar firmante
  const confirmDeleteFirmante = (id) => {
    setConfirmAction(() => () => deleteFirmante(id));
    setConfirmMessage(
      "¿Está seguro de eliminar este firmante? Esta acción no se puede deshacer."
    );
    setConfirmOpen(true);
  };

  const handleOpenHistory = (firmante) => {
    setSelectedFirmanteForHistory(firmante);
    setOpenHistoryModal(true);
  };

  const deleteFirmante = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signers/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al eliminar firmante");
      }

      // Actualizar lista de firmantes
      const updatedFirmantes = firmantes.filter(
        (firmante) => firmante.id !== id
      );
      setFirmantes(updatedFirmantes);
      enqueueSnackbar("Firmante eliminado correctamente", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Error al eliminar firmante", { variant: "error" });
    }
  };

  // Manejo de cambio de página en la paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejo de cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          Gestión de Firmantes
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={() => {
              setEditingFirmante(null);
              setOpenUserModal(true);
            }}
            sx={{
              mr: 2,
              color: "#99235c",
              borderColor: "#99235c",
              "&:hover": {
                borderColor: "#7a1e49",
                backgroundColor: "rgba(153, 35, 92, 0.04)",
              },
            }}
            startIcon={<AddIcon />}
          >
            Añadir Firmante Usuario
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setEditingFirmante(null);
              setManualFirmante({
                first_name: "",
                last_name: "",
                rut: "",
                email: "",
                signing_order: 1,
                is_active: 1,
              });
              setManualErrors({});
              setOpenManualModal(true);
            }}
            sx={{
              backgroundColor: "#99235c",
              "&:hover": { backgroundColor: "#7a1e49" },
            }}
            startIcon={<AddIcon />}
          >
            Añadir Firmante Manual
          </Button>
        </Box>
      </Box>

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por nombre, RUT o email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="filter-type-label">Tipo</InputLabel>
              <Select
                labelId="filter-type-label"
                value={filterType}
                label="Tipo"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="system">Usuario Sistema</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="filter-active-label">Estado</InputLabel>
              <Select
                labelId="filter-active-label"
                value={filterActive}
                label="Estado"
                onChange={(e) => setFilterActive(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button variant="outlined" onClick={clearFilters} sx={{ mr: 1 }}>
              Limpiar Filtros
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchData}
              sx={{
                backgroundColor: "#99235c",
                "&:hover": { backgroundColor: "#7a1e49" },
              }}
            >
              Actualizar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>RUT</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Orden de Firma</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFirmantes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No hay firmantes que coincidan con los criterios de búsqueda
                </TableCell>
              </TableRow>
            ) : (
              filteredFirmantes
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((firmante) => (
                  <TableRow key={firmante.id}>
                    <TableCell>{`${firmante.first_name || ""} ${
                      firmante.last_name || ""
                    }`}</TableCell>
                    <TableCell>{formatRut(firmante.rut)}</TableCell>
                    <TableCell>{firmante.email}</TableCell>
                    <TableCell>{firmante.signing_order}</TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          firmante.is_manual ? <ManualIcon /> : <PersonIcon />
                        }
                        label={
                          firmante.is_manual ? "Manual" : "Usuario del Sistema"
                        }
                        size="small"
                        color={firmante.is_manual ? "default" : "primary"}
                        sx={
                          firmante.is_manual
                            ? {}
                            : { backgroundColor: "#99235c" }
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(firmante.is_active)}
                        onChange={() =>
                          toggleActiveFirmante(firmante.id, firmante.is_active)
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton
                          onClick={() => handleEditFirmante(firmante)}
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          onClick={() => confirmDeleteFirmante(firmante.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver historial">
                        <IconButton
                          onClick={() => handleOpenHistory(firmante)}
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredFirmantes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </Paper>

      {/* Modal para añadir/editar firmante manual */}
      <Modal
        open={openManualModal}
        onClose={() => {
          setOpenManualModal(false);
          setEditingFirmante(null);
          setManualErrors({});
        }}
        aria-labelledby="modal-add-manual-signer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: "90%",
            maxWidth: 500,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {editingFirmante
              ? "Editar Firmante Manual"
              : "Añadir Firmante Manual"}
          </Typography>
          <TextField
            label="Nombre"
            fullWidth
            margin="normal"
            name="first_name"
            value={manualFirmante.first_name}
            onChange={handleManualInputChange}
            error={!!manualErrors.first_name}
            helperText={manualErrors.first_name}
            required
          />
          <TextField
            label="Apellido"
            fullWidth
            margin="normal"
            name="last_name"
            value={manualFirmante.last_name}
            onChange={handleManualInputChange}
            error={!!manualErrors.last_name}
            helperText={manualErrors.last_name}
            required
          />
          <TextField
            label="RUT"
            fullWidth
            margin="normal"
            name="rut"
            value={manualFirmante.rut}
            onChange={handleManualInputChange}
            error={!!manualErrors.rut}
            helperText={manualErrors.rut || "Formato: XXXXXXXX-X"}
            required
            InputProps={{
              onBlur: (e) => {
                // Formatear RUT al perder el foco
                setManualFirmante((prev) => ({
                  ...prev,
                  rut: formatRut(e.target.value),
                }));
              },
            }}
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            name="email"
            value={manualFirmante.email}
            onChange={handleManualInputChange}
            error={!!manualErrors.email}
            helperText={manualErrors.email}
            required
            type="email"
          />
          <TextField
            label="Orden de Firma"
            fullWidth
            margin="normal"
            name="signing_order"
            type="number"
            value={manualFirmante.signing_order}
            onChange={handleManualInputChange}
            inputProps={{ min: 1 }}
          />
          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              onClick={() => {
                setOpenManualModal(false);
                setEditingFirmante(null);
                setManualErrors({});
              }}
              sx={{ color: "#666" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={
                editingFirmante ? handleUpdateFirmante : handleAddManualFirmante
              }
              sx={{
                backgroundColor: "#99235c",
                "&:hover": {
                  backgroundColor: "#7a1e49",
                },
              }}
            >
              {editingFirmante ? "Actualizar" : "Guardar"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal para añadir firmante usuario */}
      <Modal
        open={openUserModal}
        onClose={() => {
          setOpenUserModal(false);
          setUserErrors({});
        }}
        aria-labelledby="modal-add-user-signer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: "90%",
            maxWidth: 500,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Añadir Firmante desde Usuarios
          </Typography>
          <FormControl fullWidth margin="normal" error={!!userErrors.user}>
            <InputLabel id="user-select-label">Usuario *</InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUser}
              label="Usuario *"
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              {usuarios.map((usuario) => (
                <MenuItem key={usuario.user_id} value={usuario.user_id}>
                  {`${usuario.first_name} ${usuario.last_name} (${usuario.rut})`}
                </MenuItem>
              ))}
            </Select>
            {userErrors.user && (
              <FormHelperText>{userErrors.user}</FormHelperText>
            )}
          </FormControl>
          <TextField
            label="Orden de Firma"
            fullWidth
            margin="normal"
            type="number"
            value={signingOrder}
            onChange={(e) => setSigningOrder(e.target.value)}
            inputProps={{ min: 1 }}
          />
          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              onClick={() => {
                setOpenUserModal(false);
                setUserErrors({});
              }}
              sx={{ color: "#666" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleAddUserFirmante}
              sx={{
                backgroundColor: "#99235c",
                "&:hover": {
                  backgroundColor: "#7a1e49",
                },
              }}
            >
              Guardar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal para editar orden de firma para firmantes de usuario */}
      <Modal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setEditingFirmante(null);
        }}
        aria-labelledby="modal-edit-signing-order"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: "90%",
            maxWidth: 500,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Editar Orden de Firma
          </Typography>
          {editingFirmante && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Firmante:</strong> {editingFirmante.first_name}{" "}
                {editingFirmante.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {editingFirmante.rut}
              </Typography>
            </Box>
          )}
          <TextField
            label="Orden de Firma"
            fullWidth
            margin="normal"
            type="number"
            value={signingOrder}
            onChange={(e) => setSigningOrder(e.target.value)}
            inputProps={{ min: 1 }}
          />
          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              onClick={() => {
                setOpenEditModal(false);
                setEditingFirmante(null);
              }}
              sx={{ color: "#666" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateFirmante}
              sx={{
                backgroundColor: "#99235c",
                "&:hover": {
                  backgroundColor: "#7a1e49",
                },
              }}
            >
              Actualizar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de confirmación */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="modal-confirm-action"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: "90%",
            maxWidth: 500,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Confirmar acción
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {confirmMessage}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              sx={{ color: "#666" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (confirmAction) {
                  confirmAction();
                  setConfirmOpen(false);
                }
              }}
            >
              Confirmar
            </Button>
          </Box>
        </Box>
      </Modal>
      <SignerHistoryModal
        open={openHistoryModal}
        onClose={() => setOpenHistoryModal(false)}
        firmante={selectedFirmanteForHistory}
        token={token}
      />
    </Box>
  );
}

export default FirmantesPage;
